<!-- src/View/components/OtpInput.vue -->
<template>
  <div class="otp-container">
    <input
      v-for="(digit, index) in 6"
      :key="index"
      ref="otpInputs"
      type="text"
      inputmode="numeric"
      maxlength="1"
      class="otp-box"
      :class="{ 'active': focusedIndex === index, 'filled': otpArray[index] }"
      :value="otpArray[index] || ''"
      @input="handleInput($event, index)"
      @keydown="handleKeydown($event, index)"
      @focus="focusedIndex = index"
      @blur="focusedIndex = null"
      @paste="handlePaste"
    />
  </div>
</template>

<script>
export default {
  name: 'OtpInput',
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      otpArray: ['', '', '', '', '', ''],
      focusedIndex: null
    }
  },
  watch: {
    value(newVal) {
      // Sync if parent updates the value externally
      if (newVal && newVal.length === 6) {
        this.otpArray = newVal.split('')
      }
    }
  },
  mounted() {
    // Auto-focus first input
    this.$nextTick(() => {
      if (this.$refs.otpInputs?.[0]) {
        this.$refs.otpInputs[0].focus()
      }
    })
  },
  methods: {
    handleInput(event, index) {
      const val = event.target.value.replace(/\D/g, '').slice(0, 1)
      
      if (val) {
        // Update array
        this.$set(this.otpArray, index, val)
        this.emitOtp()
        
        // Move to next input if not last
        if (index < 5 && this.$refs.otpInputs[index + 1]) {
          this.$refs.otpInputs[index + 1].focus()
        }
      }
    },
    
    handleKeydown(event, index) {
      // Backspace: clear current & move to previous
      if (event.key === 'Backspace' && !this.otpArray[index] && index > 0) {
        event.preventDefault()
        this.$set(this.otpArray, index - 1, '')
        this.emitOtp()
        if (this.$refs.otpInputs[index - 1]) {
          this.$refs.otpInputs[index - 1].focus()
        }
      }
      // Arrow keys navigation
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.$refs.otpInputs[index - 1]?.focus()
      }
      if (event.key === 'ArrowRight' && index < 5) {
        event.preventDefault()
        this.$refs.otpInputs[index + 1]?.focus()
      }
    },
    
    handlePaste(event) {
      event.preventDefault()
      const pasted = (event.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6)
      
      if (pasted.length === 6) {
        this.otpArray = pasted.split('')
        this.emitOtp()
        // Focus last input after paste
        this.$nextTick(() => {
          this.$refs.otpInputs[5]?.focus()
        })
      }
    },
    
    emitOtp() {
      const otp = this.otpArray.join('')
      this.$emit('input', otp) // For v-model support
      this.$emit('otpComplete', otp) // When all 6 digits filled
    },
    
    // Public method to clear OTP
    clear() {
      this.otpArray = ['', '', '', '', '', '']
      this.emitOtp()
      this.$nextTick(() => {
        this.$refs.otpInputs[0]?.focus()
      })
    },
    
    // Public method to get current OTP
    getOtp() {
      return this.otpArray.join('')
    }
  }
}
</script>

<style scoped>
.otp-container {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 16px 0;
}

.otp-box {
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  outline: none;
  transition: all 0.2s ease;
  background: #fff;
  color: #333;
}

.otp-box:focus,
.otp-box.active {
  border-color: #1976d2; /* primary color */
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

.otp-box.filled {
  border-color: #4caf50;
  background: #f1f8e9;
}

/* Hide number input spinners */
.otp-box::-webkit-outer-spin-button,
.otp-box::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.otp-box[type=number] {
  -moz-appearance: textfield;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .otp-box {
    width: 40px;
    height: 48px;
    font-size: 20px;
  }
  .otp-container {
    gap: 8px;
  }
}
</style>